import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, ArrowLeft, CheckCircle2, AlertTriangle, Clock, CalendarClock, Gavel } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const P = '#7C3AED';

const STATUS_META = {
  current: { label: 'Current', color: '#16A34A', bg: '#16A34A15', icon: CheckCircle2 },
  due_soon: { label: 'Due soon', color: '#D97706', bg: '#D9770615', icon: Clock },
  overdue: { label: 'Overdue', color: '#DC2626', bg: '#DC262615', icon: AlertTriangle },
};

function fmt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function GovernanceDashboardPage() {
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [data, setData] = useState({ rows: [], summary: { total: 0, overdue: 0, due_soon: 0, current: 0 } });
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await fetch(`${API}/sops-governance/overview`, { credentials: 'include' });
    if (r.ok) setData(await r.json());
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const markReviewed = async (sid) => {
    if (!window.confirm('Confirm this SOP has been reviewed today? The 12-month review clock will reset.')) return;
    setSavingId(sid);
    try {
      await fetch(`${API}/sops/${sid}/review`, { method: 'POST', credentials: 'include' });
      await load();
    } finally { setSavingId(null); }
  };

  const rolloutClinical = async (r) => {
    if (!window.confirm(`Re-assign "${r.title}" (v${r.version}) to every radiologist, radiographer, sonographer and nurse, due in 14 days? Each person gets a reminder email.`)) return;
    const reason = window.prompt('Reason shown to staff:', 'Updated after NSW Health letter HA26-4074 (septic arthritis / IPAC)');
    if (reason === null) return;
    const d = new Date(); d.setDate(d.getDate() + 14);
    setSavingId(r.sop_id);
    try {
      const res = await fetch(`${API}/sops/${r.sop_id}/assign/clinical`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ due_date: d.toISOString().slice(0, 10), reason }),
      });
      const j = await res.json();
      window.alert(res.ok ? `Assigned to ${j.assigned} clinical staff.` : (j.detail || 'Failed'));
      await load();
    } finally { setSavingId(null); }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background" data-testid="governance-page">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/sops" data-testid="gov-back" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-5">
          <ArrowLeft className="w-4 h-4" /> Back to SOPs
        </Link>

        <div className="rounded-2xl border p-6 mb-6" style={{ borderColor: `${P}33`, background: `linear-gradient(135deg, ${P}0A, transparent 60%)` }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${P}15`, color: P }}>
              <ShieldCheck className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full" style={{ backgroundColor: `${P}1A`, color: P }}>SOPs · Governance Dashboard</span>
          </div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Clinical Governance Overview</h1>
          <p className="text-sm text-muted-foreground mt-1">Every SOP — version, ISO reference, owner, review status, and days until next review. Medical Director can mark each as reviewed to reset the 12-month clock.</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="gov-summary">
          <Kpi label="Total SOPs" value={data.summary.total} color={P} />
          <Kpi label="Overdue" value={data.summary.overdue} color={data.summary.overdue ? '#DC2626' : '#94A3B8'} />
          <Kpi label="Due soon (≤30d)" value={data.summary.due_soon} color={data.summary.due_soon ? '#D97706' : '#94A3B8'} />
          <Kpi label="Current" value={data.summary.current} color="#16A34A" />
          <Kpi label="Flagged by incidents" value={data.rows.filter(r => r.linked_incidents_count > 0).length}
            color={data.rows.some(r => r.linked_incidents_count > 0) ? '#DC2626' : '#94A3B8'} />
        </div>

        <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="gov-table">
          {loading ? (
            <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
          ) : data.rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">No SOPs registered.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">SOP</th>
                  <th className="px-3 py-2">Version</th>
                  <th className="px-3 py-2">Owner</th>
                  <th className="px-3 py-2">Last reviewed</th>
                  <th className="px-3 py-2">Due</th>
                  {isPM && <th className="px-3 py-2"></th>}
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => {
                  const m = STATUS_META[r.status];
                  const Icon = m.icon;
                  return (
                    <tr key={r.sop_id} className="border-b border-border/60 last:border-0" data-testid={`gov-row-${i}`}>
                      <td className="px-3 py-2">
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: m.bg, color: m.color }}>
                          <Icon className="w-3 h-3" /> {m.label}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-start gap-2">
                          <div className="min-w-0">
                            <p className="font-semibold text-foreground">{r.title}</p>
                            <p className="text-[11px] text-muted-foreground">{r.category}{r.iso_reference ? ` · ${r.iso_reference}` : ''}</p>
                          </div>
                          {r.linked_incidents_count > 0 && (
                            <span className="inline-flex items-center gap-1 shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                              style={{ backgroundColor: '#DC262615', color: '#DC2626' }}
                              title="Flagged for early review from incident report"
                              data-testid={`gov-incident-flag-${i}`}>
                              <AlertTriangle className="w-2.5 h-2.5" /> {r.linked_incidents_count} incident{r.linked_incidents_count === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-foreground font-mono text-xs">v{r.version}</td>
                      <td className="px-3 py-2 text-xs text-foreground">
                        {r.owner_role}
                        {r.governance_lead && r.governance_lead !== '—' && (
                          <p className="text-[10px] text-muted-foreground mt-0.5">Lead: {r.governance_lead}</p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <p className="text-foreground">{fmt(r.last_reviewed_at || r.updated_at)}</p>
                        <p className="text-[10px] text-muted-foreground">by {r.last_reviewed_by || 'system'}</p>
                      </td>
                      <td className="px-3 py-2">
                        <p className="text-sm text-foreground" style={{ color: m.color }}>
                          {r.days_until_review < 0 ? `${Math.abs(r.days_until_review)} days overdue` : `in ${r.days_until_review} days`}
                        </p>
                        <p className="text-[10px] text-muted-foreground"><CalendarClock className="inline w-2.5 h-2.5 mr-0.5" />{fmt(r.review_due)}</p>
                      </td>
                      {isPM && (
                        <td className="px-3 py-2">
                          <button onClick={() => markReviewed(r.sop_id)} disabled={savingId === r.sop_id}
                            data-testid={`gov-review-${i}`}
                            className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-50"
                            style={{ backgroundColor: P }}>
                            <Gavel className="w-3 h-3" /> {savingId === r.sop_id ? 'Saving…' : 'Mark reviewed'}
                          </button>
                          <button onClick={() => rolloutClinical(r)} disabled={savingId === r.sop_id}
                            data-testid={`gov-rollout-${i}`}
                            title="Re-assign to all radiologists, radiographers, sonographers and nurses (due in 14 days, emails each person)"
                            className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-lg border disabled:opacity-50"
                            style={{ borderColor: P, color: P }}>
                            Re-assign to clinical staff
                          </button>
                          {r.last_clinical_rollout_at && (
                            <p className="text-[10px] text-muted-foreground mt-1" data-testid={`gov-rollout-info-${i}`}>
                              Sent to {r.last_clinical_rollout_count} · {fmt(r.last_clinical_rollout_at)}
                            </p>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, color }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
      <p className="font-['Outfit'] text-2xl font-black" style={{ color }}>{value}</p>
    </div>
  );
}
